import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  LinearProgress,
  Divider,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Breadcrumbs,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as LightbulbIcon,
  OpenInNew as OpenInNewIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import type { Mitigation, CreateMitigationDto } from 'shared';
import { ATTACK_TECHNIQUES, getAttackUrl, EntityType } from 'shared';
import {
  useRisk,
  useMitigationsByRisk,
  useCreateMitigation,
  useUpdateMitigation,
} from '../hooks/useRisks';
import { componentsService } from '../services/components.service';
import { MitigationForm } from '../components/MitigationForm';
import { VersionHistoryDialog } from '../components/History';
import { RiskForm } from '../components/RiskForm';
import { useSpace } from '../contexts/SpaceContext';
import { useSecurityAssistant } from '../contexts/SecurityAssistantContext';
import { riskApi } from '../services/risk.service';

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface ScoreChipProps {
  score: number | string | undefined;
  showLabel?: boolean;
  size?: 'small' | 'medium';
}

const RiskLevelChip: React.FC<ScoreChipProps> = ({
  score,
  showLabel = true,
  size = 'small',
}) => {
  const { t } = useTranslation();
  const numScore =
    typeof score === 'number' ? score : parseFloat(String(score)) || 0;

  const getRiskLevel = (
    s: number
  ): { label: string; color: string; bgColor: string } => {
    if (s >= 8)
      return {
        label: t('risk.level.critical'),
        color: '#FE3B00',
        bgColor: '#FE3B0020',
      };
    if (s >= 6)
      return {
        label: t('risk.level.high'),
        color: '#2D3B45',
        bgColor: '#2D3B4520',
      };
    if (s >= 4)
      return {
        label: t('risk.level.medium'),
        color: '#FFBB00',
        bgColor: '#FFBB0020',
      };
    if (s >= 2)
      return {
        label: t('risk.level.low'),
        color: '#42B8B2',
        bgColor: '#42B8B220',
      };
    return {
      label: t('risk.level.minimal'),
      color: '#939597',
      bgColor: '#93959720',
    };
  };

  const { label, color, bgColor } = getRiskLevel(numScore);

  return (
    <Chip
      label={
        showLabel ? `${numScore.toFixed(1)} - ${label}` : numScore.toFixed(1)
      }
      size={size}
      sx={{
        backgroundColor: bgColor,
        color: color,
        fontWeight: 600,
      }}
    />
  );
};

interface CIAScoreBarProps {
  label: string;
  score: number | string | undefined;
  color: string;
}

const CIAScoreBar: React.FC<CIAScoreBarProps> = ({ label, score, color }) => {
  const numScore =
    typeof score === 'number' ? score : parseFloat(String(score)) || 0;
  const percentage = (numScore / 10) * 100;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ color }}>
          {numScore.toFixed(1)}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: `${color}20`,
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
};

interface MitigationStatusChipProps {
  status: string;
}

const MitigationStatusChip: React.FC<MitigationStatusChipProps> = ({
  status,
}) => {
  const { t } = useTranslation();

  const getStatusConfig = (s: string) => {
    switch (s) {
      case 'verified':
        return {
          color: '#42B8B2',
          bgColor: '#42B8B220',
          icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
        };
      case 'implemented':
        return {
          color: '#42B8B2',
          bgColor: '#42B8B220',
          icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
        };
      case 'in_progress':
        return {
          color: '#FFBB00',
          bgColor: '#FFBB0020',
          icon: <ScheduleIcon sx={{ fontSize: 14 }} />,
        };
      case 'planned':
      default:
        return {
          color: '#939597',
          bgColor: '#93959720',
          icon: <ScheduleIcon sx={{ fontSize: 14 }} />,
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      icon={config.icon}
      label={t(`mitigation.status.${status}`)}
      size="small"
      sx={{
        backgroundColor: config.bgColor,
        color: config.color,
        '& .MuiChip-icon': { color: config.color },
      }}
    />
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const RiskDetail: React.FC = () => {
  const { riskId } = useParams<{ riskId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();
  const { setRiskContext, clearContext } = useSecurityAssistant();

  const [mitigationFormOpen, setMitigationFormOpen] = useState(false);
  const [editingMitigation, setEditingMitigation] = useState<Mitigation | null>(
    null
  );
  const [selectedMitigation, setSelectedMitigation] =
    useState<Mitigation | null>(null);
  const [riskFormOpen, setRiskFormOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState(false);
  const [ownerValue, setOwnerValue] = useState('');
  const [savingOwner, setSavingOwner] = useState(false);

  // Fetch risk data
  const {
    data: risk,
    isLoading: riskLoading,
    error: riskError,
  } = useRisk(riskId || '');

  // Fetch component data if risk has componentId
  const { data: component } = useQuery({
    queryKey: ['component', risk?.componentId],
    queryFn: () => {
      if (!risk?.componentId) throw new Error('No component ID');
      return componentsService.getById(risk.componentId);
    },
    enabled: !!risk?.componentId,
  });

  // Fetch mitigations
  const {
    data: mitigations = [],
    isLoading: mitigationsLoading,
    error: mitigationsError,
  } = useMitigationsByRisk(riskId || '');

  // Set Security Assistant context when risk is loaded
  useEffect(() => {
    if (risk && currentSpace) {
      setRiskContext({
        spaceId: currentSpace.id,
        riskId: risk.id,
        riskTitle: risk.title,
        componentId: risk.componentId,
        componentName: component?.name,
        threatSource: risk.threatSource,
        vulnerability: risk.vulnerability,
        inherentRisk: risk.inherentRisk,
        residualRisk: risk.residualRisk,
        riskStatus: risk.status,
        mitigationCount: mitigations.length,
      });
    }
    // Clear context when leaving the page
    return () => {
      clearContext();
    };
  }, [
    risk,
    component,
    mitigations.length,
    currentSpace,
    setRiskContext,
    clearContext,
  ]);

  const createMitigation = useCreateMitigation();
  const updateMitigation = useUpdateMitigation();

  // Calculate effectiveness
  const activeMitigations = mitigations.filter(
    (m: Mitigation) =>
      m.implementationStatus === 'implemented' ||
      m.implementationStatus === 'verified'
  );

  const totalReduction = activeMitigations.reduce(
    (acc: number, m: Mitigation) => {
      const factor = parseFloat(String(m.reductionFactor)) || 0;
      return acc * (1 - factor);
    },
    1
  );

  const effectivenessPercent = Math.round((1 - totalReduction) * 100);

  const handleMitigationSubmit = async (
    data: CreateMitigationDto | Partial<Omit<CreateMitigationDto, 'riskId'>>
  ) => {
    if (!riskId) return;

    if (editingMitigation) {
      // Edit mode - update existing mitigation
      await updateMitigation.mutateAsync({
        id: editingMitigation.id,
        data: data as CreateMitigationDto,
      });
    } else {
      // Create mode - add riskId and create new
      const fullData = {
        ...data,
        riskId: riskId,
      } as CreateMitigationDto;
      await createMitigation.mutateAsync(fullData);
    }

    setMitigationFormOpen(false);
    setEditingMitigation(null);
    queryClient.invalidateQueries({
      queryKey: ['mitigations', 'risk', riskId],
    });
    queryClient.invalidateQueries({ queryKey: ['risk', riskId] });
  };

  const handleMitigationClick = (mitigation: Mitigation) => {
    // Set focus on single click
    setSelectedMitigation(mitigation);
  };

  const handleMitigationDoubleClick = (mitigationId: string) => {
    // Navigate to mitigation detail page on double click
    navigate(`/mitigations/${mitigationId}`);
  };

  const handleAddMitigation = () => {
    setEditingMitigation(null);
    setMitigationFormOpen(true);
  };

  const handleMitigationFormClose = () => {
    setMitigationFormOpen(false);
    setEditingMitigation(null);
  };

  const handleRiskUpdate = async () => {
    queryClient.invalidateQueries({ queryKey: ['risk', riskId] });
    setRiskFormOpen(false);
  };

  // Handle owner inline edit
  const handleStartEditOwner = () => {
    setOwnerValue(risk?.owner || '');
    setEditingOwner(true);
  };

  const handleCancelEditOwner = () => {
    setEditingOwner(false);
    setOwnerValue('');
  };

  const handleSaveOwner = async () => {
    if (!riskId) return;
    setSavingOwner(true);
    try {
      await riskApi.update(riskId, { owner: ownerValue });
      queryClient.invalidateQueries({ queryKey: ['risk', riskId] });
      setEditingOwner(false);
    } catch (error) {
      console.error('Failed to update owner:', error);
    } finally {
      setSavingOwner(false);
    }
  };

  // Loading state
  if (riskLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (riskError || !risk) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('risk.detail.notFound')}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/risks')}
          sx={{ mt: 2 }}
        >
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/risks')}
        >
          {t('nav.risks')}
        </Link>
        <Typography color="text.primary">{risk.title}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={600}>
                {risk.title}
              </Typography>
              {component && (
                <Typography variant="body2" color="text.secondary">
                  {t('risk.fields.component')}:{' '}
                  <Link
                    component="button"
                    onClick={() => navigate(`/components/${component.id}`)}
                    sx={{ textDecoration: 'underline' }}
                  >
                    {component.name}
                  </Link>
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('history.viewHistory')}>
              <IconButton onClick={() => setHistoryDialogOpen(true)}>
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setRiskFormOpen(true)}
            >
              {t('common.edit')}
            </Button>
          </Box>
        </Box>

        {/* Status chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={t(`risk.status.${risk.status}`)}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={t(`risk.treatment.${risk.riskTreatment}`)}
            size="small"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Risk Scores & CIA */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Risk Scores Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SecurityIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                {t('risk.detail.riskScores')}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('risk.fields.inherentRisk')}
                </Typography>
                <RiskLevelChip score={risk.inherentRisk} size="medium" />
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t('risk.detail.inherentRiskHelp')}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('risk.fields.residualRisk')}
                </Typography>
                <RiskLevelChip score={risk.residualRisk} size="medium" />
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t('risk.detail.residualRiskHelp')}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('risk.fields.likelihood')}
              </Typography>
              <Typography variant="h6">
                {parseFloat(String(risk.likelihood) || '0').toFixed(1)} / 10
              </Typography>
            </CardContent>
          </Card>

          {/* CIA Impact Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('risk.detail.ciaImpact')}
              </Typography>

              <CIAScoreBar
                label={t('classification.confidentiality.title')}
                score={risk.confidentialityImpact}
                color="#2D3B45"
              />
              <CIAScoreBar
                label={t('classification.integrity.title')}
                score={risk.integrityImpact}
                color="#42B8B2"
              />
              <CIAScoreBar
                label={t('classification.availability.title')}
                score={risk.availabilityImpact}
                color="#FFBB00"
              />
            </CardContent>
          </Card>

          {/* Effectiveness Card */}
          {activeMitigations.length > 0 && (
            <Card sx={{ backgroundColor: '#42B8B210' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ShieldIcon sx={{ color: '#42B8B2', fontSize: 40 }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('risk.detail.combinedEffectiveness')}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ color: '#42B8B2', fontWeight: 600 }}
                    >
                      {effectivenessPercent}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('risk.detail.riskReduction')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Details & Mitigations */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Risk Metadata - Quick Info */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('risk.detail.metadata')}
            </Typography>
            <Grid container spacing={2} alignItems="center">
              {/* Owner with inline edit */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('risk.fields.owner')}
                </Typography>
                {editingOwner ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                      size="small"
                      value={ownerValue}
                      onChange={e => setOwnerValue(e.target.value)}
                      placeholder={t('risk.fields.owner')}
                      autoFocus
                      sx={{ minWidth: 120 }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleSaveOwner();
                        if (e.key === 'Escape') handleCancelEditOwner();
                      }}
                    />
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleSaveOwner}
                      disabled={savingOwner}
                    >
                      {savingOwner ? (
                        <CircularProgress size={16} />
                      ) : (
                        <SaveIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEditOwner}
                      disabled={savingOwner}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                    />
                    <Typography variant="body1">
                      {risk.owner || t('common.none')}
                    </Typography>
                    <Tooltip title={t('common.edit')}>
                      <IconButton size="small" onClick={handleStartEditOwner}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Grid>

              {/* Review Date */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('risk.fields.reviewDate')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography variant="body1">
                    {risk.reviewDate
                      ? new Date(risk.reviewDate).toLocaleDateString()
                      : t('common.none')}
                  </Typography>
                </Box>
              </Grid>

              {/* Created */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('common.createdAt')}
                </Typography>
                <Typography variant="body1">
                  {new Date(risk.createdAt).toLocaleDateString()}
                </Typography>
              </Grid>

              {/* Updated */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('common.updatedAt')}
                </Typography>
                <Typography variant="body1">
                  {new Date(risk.updatedAt).toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Description */}
          {risk.description && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('risk.fields.description')}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {risk.description}
              </Typography>
            </Paper>
          )}

          {/* Threat Analysis */}
          <Accordion defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <WarningIcon
                  sx={{ verticalAlign: 'middle', mr: 1, color: '#FE3B00' }}
                />
                {t('risk.detail.threatAnalysis')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {risk.threatSource && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('risk.fields.threatSource')}
                    </Typography>
                    <Typography variant="body1">{risk.threatSource}</Typography>
                  </Grid>
                )}
                {risk.vulnerability && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('risk.fields.vulnerability')}
                    </Typography>
                    <Typography variant="body1">
                      {risk.vulnerability}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {risk.existingControls && risk.existingControls.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t('risk.fields.existingControls')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {risk.existingControls.map(
                      (control: string, index: number) => (
                        <Chip
                          key={index}
                          label={control}
                          size="small"
                          variant="outlined"
                          icon={<ShieldIcon />}
                        />
                      )
                    )}
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* MITRE ATT&CK Reference */}
          {risk.attackTechniqueId && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: '#FE3B0010',
                borderColor: '#FE3B0040',
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('risk.detail.mitreAttack')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="h6" fontWeight={500}>
                  {risk.attackTechniqueId}:{' '}
                  {ATTACK_TECHNIQUES.find(
                    tech => tech.id === risk.attackTechniqueId
                  )?.name || t('common.unknown')}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                  href={getAttackUrl(risk.attackTechniqueId)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('risk.detail.viewOnMitre')}
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Mitigations Section - Full Width */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">
            <CheckCircleIcon
              sx={{ verticalAlign: 'middle', mr: 1, color: '#42B8B2' }}
            />
            {t('risk.detail.mitigations')} ({mitigations.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMitigation}
          >
            {t('mitigation.create')}
          </Button>
        </Box>

        {mitigationsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : mitigationsError ? (
          <Alert severity="error">{t('mitigation.loadError')}</Alert>
        ) : mitigations.length === 0 ? (
          <Alert severity="info" icon={<LightbulbIcon />}>
            {t('risk.detail.noMitigations')}
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('mitigation.fields.title')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('mitigation.fields.controlType')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('mitigation.fields.isoControlId')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    {t('mitigation.fields.effectiveness')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('mitigation.fields.status')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('mitigation.fields.owner')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('mitigation.fields.dueDate')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mitigations.map((mitigation: Mitigation) => (
                  <TableRow
                    key={mitigation.id}
                    hover
                    selected={selectedMitigation?.id === mitigation.id}
                    onClick={() => handleMitigationClick(mitigation)}
                    onDoubleClick={() =>
                      handleMitigationDoubleClick(mitigation.id)
                    }
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {mitigation.title}
                      </Typography>
                      {mitigation.description && (
                        <Typography variant="caption" color="text.secondary">
                          {mitigation.description.substring(0, 80)}
                          {mitigation.description.length > 80 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(
                          `mitigation.controlType.${mitigation.controlType}`
                        )}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {mitigation.isoControlId ? (
                        <Tooltip title={mitigation.isoControlName || ''}>
                          <Chip
                            label={mitigation.isoControlId}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          color:
                            mitigation.implementationStatus === 'implemented' ||
                            mitigation.implementationStatus === 'verified'
                              ? '#42B8B2'
                              : 'text.secondary',
                        }}
                      >
                        {Math.round(
                          (parseFloat(String(mitigation.reductionFactor)) ||
                            0) * 100
                        )}
                        %
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <MitigationStatusChip
                        status={mitigation.implementationStatus}
                      />
                    </TableCell>
                    <TableCell>
                      {mitigation.owner && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <PersonIcon
                            sx={{ fontSize: 14, color: 'text.secondary' }}
                          />
                          <Typography variant="body2">
                            {mitigation.owner}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {mitigation.dueDate && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <CalendarIcon
                            sx={{ fontSize: 14, color: 'text.secondary' }}
                          />
                          <Typography variant="body2">
                            {new Date(mitigation.dueDate).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Mitigation Form Modal */}
      {riskId && (
        <MitigationForm
          open={mitigationFormOpen}
          onClose={handleMitigationFormClose}
          onSubmit={handleMitigationSubmit}
          riskId={riskId}
          mode={editingMitigation ? 'edit' : 'create'}
          initialData={editingMitigation || undefined}
        />
      )}

      {/* Risk Edit Form */}
      {currentSpace && risk && (
        <RiskForm
          open={riskFormOpen}
          onClose={() => setRiskFormOpen(false)}
          onSubmit={async data => {
            await riskApi.update(risk.id, data);
            handleRiskUpdate();
          }}
          componentId={risk.componentId}
          spaceId={currentSpace.id}
          mode="edit"
          initialData={risk}
        />
      )}

      {/* Version History Dialog */}
      {risk && (
        <VersionHistoryDialog
          open={historyDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
          entityType={EntityType.RISK}
          entityId={risk.id}
          entityName={risk.title}
        />
      )}
    </Box>
  );
};
